import type {ReactNode} from 'react'
import {PullRequestState} from '../../../../types'
import {ClosedPR, shouldShowClosedPR} from '../pull-request-state/ClosedPR'
import {MergedPR, shouldShowMergedPR} from '../pull-request-state/MergedPR'
import {AbleToMerge, shouldShowAbleToMerge} from '../mergeability/AbleToMerge'
import {ApprovableChecks} from '../mergeability/ApprovableChecks'
import {AwaitingApprovalChecks} from '../mergeability/AwaitingApprovalChecks'
import {shouldShowDraftPR, DraftPR} from '../pull-request-state/DraftPR'
import {FailingChecks} from '../mergeability/FailingChecks'
import {
  shouldShowBlockedByDownstackPullRequest,
  BlockedByDownstackPullRequest,
} from '../mergeability/BlockedByDownstackPullRequest'
import {shouldShowMergeBlocked, MergeBlocked} from '../mergeability/MergeBlocked'
import {shouldShowMergeConflicts, MergeConflicts} from '../mergeability/MergeConflicts'
import {shouldShowReadyToMerge, ReadyToMerge} from '../mergeability/ReadyToMerge'
import {ReviewSubmitted} from '../mergeability/ReviewSubmitted'
import {UpdateBranch} from '../mergeability/UpdateBranch'
import {getBranchUpdateRequirement} from '../mergeability/shared/OverviewUpdateBranchControl'
import {decideChecksItem} from '../mergeability/shared/checks-item-decider'
import {decideReviewSubmittedItem} from '../mergeability/shared/review-submitted-item-decider'
import {selectFailedRequiredChecks} from '../mergeability/use-failed-checks-data'
import {useMergeabilityData, type MergeabilityData} from '../mergeability/use-mergeability-data'
import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {QueuedPR, shouldShowQueuedPR} from '../pull-request-state/QueuedPR'
import {useUnresolvedConversationCount} from '../../../../page-data/loaders/use-tab-counts'
import {StackClosed, shouldShowStackClosed} from '../stack/StackClosed'
import {useLayoutQuery} from '../../../../page-data/loaders/use-layout-query'
import type {StackData} from '../../../../page-data/payloads/header'
import {PendingChecks} from '../mergeability/PendingChecks'

type AuthorForYouActionInput = {
  mergeabilityData: MergeabilityData | null
  pullRequestState: PullRequestState
  pullRequestNumber?: number
  stack?: StackData | null
  hasUnresolvedThreads?: boolean
}

type LazyAction = {
  action: ReactNode
  matches: () => boolean
}

export function useAuthorOrAssigneeDecider() {
  const mergeabilityData = useMergeabilityData()
  const pullRequestState = usePullRequestState()
  const unresolvedThreadCount = useUnresolvedConversationCount() ?? 0
  const {pullRequest, stack} = useLayoutQuery().data

  return getAuthorForYouAction({
    mergeabilityData,
    pullRequestState,
    pullRequestNumber: pullRequest.number,
    stack,
    hasUnresolvedThreads: unresolvedThreadCount > 0,
  })
}

export function getAuthorForYouAction({
  mergeabilityData,
  pullRequestState,
  pullRequestNumber = 0,
  stack = null,
  hasUnresolvedThreads = false,
}: AuthorForYouActionInput): ReactNode {
  const decisionTree: LazyAction[] = [
    {action: <ClosedPR />, matches: () => shouldShowClosedPR(pullRequestState)},
    {
      action: <MergedPR />,
      matches: () => shouldShowMergedPR(pullRequestState),
    },
    {
      action: <ApprovableChecks />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'approvable',
    },
    {
      action: <QueuedPR />,
      matches: () => shouldShowQueuedPR(mergeabilityData),
    },
    {action: <MergeConflicts />, matches: () => shouldShowMergeConflicts(mergeabilityData)},
    {
      action: <StackClosed />,
      matches: () => shouldShowStackClosed(stack, pullRequestNumber),
    },
    {action: <DraftPR />, matches: () => shouldShowDraftPR(pullRequestState)},
    {
      action: <FailingChecks />,
      matches: () => Boolean(mergeabilityData && selectFailedRequiredChecks(mergeabilityData.statusChecks).length),
    },
    {
      action: <AwaitingApprovalChecks />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'awaiting-approval',
    },
    {
      action: <ReviewSubmitted />,
      matches: () => {
        const {kind} = decideReviewSubmittedItem(mergeabilityData, hasUnresolvedThreads)
        return kind !== 'none' && kind !== 'address-comments'
      },
    },
    {
      action: <UpdateBranch />,
      matches: () => getBranchUpdateRequirement(mergeabilityData) === 'required',
    },
    {
      action: <BlockedByDownstackPullRequest />,
      matches: () => shouldShowBlockedByDownstackPullRequest(mergeabilityData),
    },
    {action: <MergeBlocked />, matches: () => shouldShowMergeBlocked(mergeabilityData)},
    {action: <ReadyToMerge />, matches: () => shouldShowReadyToMerge(mergeabilityData)},
    {action: <AbleToMerge />, matches: () => shouldShowAbleToMerge(mergeabilityData)},
    {
      action: <PendingChecks />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'pending',
    },
  ]

  return decisionTree.find(decision => decision.matches())?.action ?? null
}
